import { useCallback, useEffect, useMemo, useState } from 'react';
import { donations } from 'src/backend';
import {
  DONATION_ERROR,
  classifyDonationError,
  getDonationUnavailableReason,
} from 'src/utils/donationAvailability';

export default function useDonation({ id, rescueId, getArray = false }) {
  const [donation, setDonation] = useState(null);
  const [error, setError] = useState(null);
  const [valid, setValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Set when the request succeeded but matched no donation — a bad id, or a
  // rescueId that no longer points at an active rescue. Without this the UI
  // renders an empty donation shell as though it were a real, claimable one.
  const [missing, setMissing] = useState(false);

  useEffect(() => setValid(false), [id, rescueId]);

  useEffect(() => {
    if (!id) {
      setDonation(null);
      return;
    }

    if (valid) {
      return;
    }

    setValid(true);
    setIsLoading(true);
    setError(null);
    setMissing(false);

    donations
      .getById(id, { rescueId, getArray })
      .then((data) => {
        const isEmpty = getArray
          ? !Array.isArray(data) || data.length === 0
          : !data;
        setMissing(isEmpty);
        setDonation(isEmpty ? null : data);
      })
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [id, valid]);

  const invalidate = useCallback(() => setValid(false), [setValid]);

  const errorKind = useMemo(() => {
    if (error) return classifyDonationError(error);
    return missing ? DONATION_ERROR.NOT_FOUND : null;
  }, [error, missing]);

  const unavailableReason = useMemo(
    () => getDonationUnavailableReason(error),
    [error]
  );

  return {
    donation,
    isLoading,
    invalidate,
    error,
    /** One of DONATION_ERROR, or null when the donation loaded. */
    errorKind,
    /**
     * Why the donation is unavailable, when the server named a reason this
     * client knows; null otherwise (generic copy applies).
     */
    unavailableReason,
    /** Re-run the request; used by the "Try again" action. */
    retry: invalidate,
  };
}
